import { Box, Tooltip, Typography, useMediaQuery, IconButton, Collapse } from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import styles from "./ColorBoxes.module.scss";
import { useMemo, useState, useRef } from "react";
import _ from "lodash";
import { useDashboard } from "../../../context/DashboardContext";
// import { isOnlyHospitalTabAccess } from "../../../utilis/common";
import MobileColorBoxDialog from "./MobileColorBoxDialog";
import useTutorialTrigger from "../../../hooks/useTutorialTrigger";
import { TUTORIAL_FEATURES } from "../../../config/tutorials.config";
import { PAGE_TYPE } from "../../../types/pages.type";
import { OVERALL_CARDS_TYPE } from "../../../types/overall.type";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";


const DateDifference = ({ comparisonRanges }) => {
	if (comparisonRanges && comparisonRanges.length > 0) {
		const { startDate, endDate } = comparisonRanges[0];

		// Validate startDate and endDate before processing
		if (startDate && endDate) {
			const formattedStartDate = moment(startDate).format("MM/DD/YYYY");
			const formattedEndDate = moment(endDate).format("MM/DD/YYYY");
			const daysDifference = moment(endDate).diff(moment(startDate), "days");

			return (
				<Typography variant="caption" fontWeight="normal" color={"#fff"} sx={{
					fontSize: "11px",
				}}>
					{`${formattedStartDate} - ${formattedEndDate} (${daysDifference} days)`}
				</Typography>
			);
		}
	}

	return null // Fallback when no valid data
};

// Common function to format value with plus sign for OVERALL page TOTAL transferType
const formatValueWithPlusSign = (value, page, transferType, customLabel = "Transfers", isTotalOverallCard = false) => {
	const scaledValue = value || 0;
	if (isTotalOverallCard) {
		return `${scaledValue} ${customLabel}`;
	}
	// Check if value is already a string with a plus sign
	const alreadyHasPlus = typeof scaledValue === 'string' && scaledValue.trim().startsWith('+');
	// Convert to number for comparison if it's a string (remove + sign if present)
	const numericValue = typeof scaledValue === 'string' 
		? parseFloat(scaledValue.replace(/^\+/, '')) || 0
		: scaledValue;
	const shouldShowPlus = page === PAGE_TYPE.OVERALL && 
		transferType === OVERALL_CARDS_TYPE.TOTAL && 
		numericValue > 0 &&
		!alreadyHasPlus;
	// If already has plus, use as is; otherwise add plus if needed, or use numeric value
	const displayValue = alreadyHasPlus 
		? scaledValue 
		: (shouldShowPlus ? `+${numericValue}` : scaledValue);
	const separator = customLabel === "%" ? "" : " ";
	return `${displayValue}${separator}${customLabel}`;
};

const TooltipTitle = ({
	comparingAgainst,
	comparingAgainstScaled,
	numberOfDays,
	comparisonRanges,
	matchingNames,
	comparingAgainstDifferentFacility,
	customLabel,
	hideProjection,
	onClickingInside,
	isTotalOverallCard = false,
	hideArrow = false,
	isPercentage = false,
}) => {
	const { dashboardData: { dbData = null, dbDataComparison = null, transferType = null, page = null } } = useDashboard();
	const [isExpanded, setIsExpanded] = useState(false);
	
	// const isOnlyHospitalDashboard = useMemo(() => {
	// 	return isOnlyHospitalTabAccess();
	// }, []);

	const comparingAgainstScaledByCensus = useMemo(() => {
		if (
			!isPercentage &&
			comparingAgainstScaled &&
			dbDataComparison &&
			!_.isEmpty(dbDataComparison) &&
			comparingAgainstDifferentFacility &&
			dbData?.censusAverage
		) {
			const currentCensus = dbDataComparison.censusAverage / dbData.censusAverage;
			return Math.round(comparingAgainstScaled / currentCensus) || 0;
		}
		return comparingAgainstScaled;
	}, [comparingAgainstScaled, dbDataComparison, dbData, comparingAgainstDifferentFacility, isPercentage]);

	const handleToggleExpand = (e) => {
		e.stopPropagation();
		e.preventDefault();
		// Mark that we're clicking inside the tooltip - set it BEFORE the state update
		if (onClickingInside) {
			onClickingInside(true);
			// Keep it set longer to ensure close handler sees it
			setTimeout(() => {
				onClickingInside(false);
			}, 300);
		}
		setIsExpanded(!isExpanded);
	};

	const handleContainerClick = (e) => {
		// Prevent clicks inside the tooltip from closing it
		e.stopPropagation();
	};

	const handleContainerMouseDown = (e) => {
		// Prevent mousedown events from closing the tooltip
		e.stopPropagation();
	};

	return (
		<div 
			className={styles.tooltipWrapper} 
			onClick={handleContainerClick}
			onMouseDown={handleContainerMouseDown}
		>
			<div 
				onClick={handleContainerClick}
				onMouseDown={handleContainerMouseDown}
			>
				<div 
					onClick={handleContainerClick}
					onMouseDown={handleContainerMouseDown}
				>
					{/* Expand/Collapse arrow button - moved to top */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							marginBottom: "4px",
						}}
					>
						{!hideArrow && (
							<IconButton
								onClick={(e) => {
									handleToggleExpand(e);
								}}
								onMouseDown={(e) => {
									e.stopPropagation();
									e.preventDefault();
									// Set flag on mousedown - this happens BEFORE click
									if (onClickingInside) {
										onClickingInside(true);
										setTimeout(() => {
											onClickingInside(false);
										}, 300);
									}
								}}
								sx={{
									color: "#fff",
									padding: "4px",
									transition: "all 0.3s ease",
									"&:hover": {
										backgroundColor: "rgba(255, 255, 255, 0.1)",
									},
								}}
								size="small"
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
										transition: "transform 0.3s ease",
									}}
								>
									<KeyboardArrowDownIcon sx={{ fontSize: "18px" }} />
								</Box>
							</IconButton>
						)}
					</Box>

					{/* Expanded details - shown when isExpanded is true with smooth animation */}
					<Collapse in={isExpanded} timeout={300}>
						<Box
							sx={{
								marginBottom: isExpanded ? "8px" : 0,
								transition: "margin-bottom 0.3s ease",
							}}
						>
							{matchingNames && (
								<Typography
									variant="body1"
									fontWeight="bold"
									color={"#fff"}
									sx={{
										fontSize: "11px"
									}}
								>
									<div dangerouslySetInnerHTML={{ __html: matchingNames }} />
								</Typography>
							)}
							<Typography
								variant="body1"
								sx={{
									textDecoration: "underline",
									fontSize: "11px",
								}}
								fontWeight="bold"
								color={"#fff"}>
								Comparison Date :
							</Typography>

							<DateDifference
								comparisonRanges={comparisonRanges}
							/>
							<Typography
								variant="body2"
								fontWeight="bold"
								color={"#fff"}
								sx={{
									fontSize: "14px"
								}}
							>
								{comparingAgainst || 0}{customLabel === "%" ? "" : " "}{customLabel || "Transfers"}
							</Typography>
						</Box>
					</Collapse>
					
					{/* Projection row - always shown */}
					{!hideProjection && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: "2px",
							}}
						>
							<Typography
								fontWeight="normal"
								color={"#fff"}
								sx={{
									fontSize: "10px",
									fontStyle: "italic",
								}}
							>
								{/* {dbData && !_.isEmpty(dbData) && comparingAgainstDifferentFacility ? (
									isOnlyHospitalDashboard ? (
										<>
											{numberOfDays || 0} Day Projection / Bed Capacity - {dbData?.bedCapacity || 0} :
										</>
									) : (
										<>
											{numberOfDays || 0} Day Projection / Avg. Census - {dbData?.censusAverage || 0} :
										</>
									)
								) : (
									<>
										{`${numberOfDays || 0} Day Projection:`}
									</>
								)} */}
								Facility Avg.
							</Typography>
							{!(dbData && !_.isEmpty(dbData) && comparingAgainstDifferentFacility) && (
								<Typography
									variant="caption"
									fontWeight="bold"
									sx={{
										fontSize: "12px",
										fontStyle: "italic",
										color: "#fff",  // Moved color to sx for consistency
									}}
								>
									{formatValueWithPlusSign(comparingAgainstScaled, page, transferType, customLabel || "Transfers", isTotalOverallCard)}
								</Typography>
							)}
						</Box>
					)}
					{!hideProjection && dbData && !_.isEmpty(dbData) && comparingAgainstDifferentFacility && (
						<Typography
							variant="body2"
							fontWeight="bold"
							sx={{
								fontSize: "13px",
								fontStyle: "italic",
							}}
							color={"#fff"}
						>
							{formatValueWithPlusSign(comparingAgainstScaledByCensus, page, transferType, customLabel || "Transfers", isTotalOverallCard)}
						</Typography>
					)}
				</div>
			</div>
		</div>
	);
};

export default function ColorBox({
	color,
	type = "div",
	barWidth,
	bar = null,
	sx,
	comparingAgainst,
	comparingAgainstScaled,
	numberOfDays,
	loading,
	loadingComparison,
	isLayoutMode,
	customLabel,
	hideProjection,
	isTotalOverallCard = false,
	hideArrow = false,
	isPercentage = false,
}) {
	const isMobile = useMediaQuery('(max-width: 768px)');
	const [mobileDialogOpen, setMobileDialogOpen] = useState(false);
	const [tooltipOpen, setTooltipOpen] = useState(null); // null = uncontrolled, true/false = controlled
	const clickingInsideRef = useRef(false); // Flag to track if clicking inside tooltip
	const { triggerTutorial, trackFeatureUsage } = useTutorialTrigger();
	const trendHighlightTutorialTriggeredRef = useRef(false);

	const {
		rangesSet,
		comparisonRanges,
		activeComparisonFacilitiesId,
		facilityByAccount,
		comparingAgainstDifferentFacility
	} = useSelector((state) => state.comparison);

	const matchingNames = useMemo(() => {
		const facilityNames = facilityByAccount
			.filter(({ _id }) => activeComparisonFacilitiesId.includes(_id))
			.map(({ name }) => name)
			.join(", ");

		const label = activeComparisonFacilitiesId.length === 1 ? "Facility Compared To" : "Facilities Compared To";

		return `<u>${label} :</u> <br /> ${facilityNames}`;
	}, [facilityByAccount, activeComparisonFacilitiesId]);

	const handleMobileClick = (event) => {
		event.preventDefault();
		event.stopPropagation();

		if (isMobile && color && rangesSet) {
			// Trigger tutorial on first mobile dialog open
			if (!trendHighlightTutorialTriggeredRef.current) {
				trendHighlightTutorialTriggeredRef.current = true;
				
				trackFeatureUsage(TUTORIAL_FEATURES.SPOTTING_TRENDS).catch((error) => {
					console.error('Failed to track spotting trends usage:', error);
				});

				triggerTutorial(TUTORIAL_FEATURES.SPOTTING_TRENDS).catch((error) => {
					console.error('Failed to trigger spotting trends tutorial:', error);
				});
			}
			
			setMobileDialogOpen(true);
		}
	};

	const handleTooltipOpen = () => {
		// Switch to controlled mode when tooltip opens
		if (tooltipOpen === null) {
			setTooltipOpen(true);
		}
		// Trigger tutorial on first tooltip hover (desktop)
		if (!trendHighlightTutorialTriggeredRef.current) {
			trendHighlightTutorialTriggeredRef.current = true;
			
			trackFeatureUsage(TUTORIAL_FEATURES.SPOTTING_TRENDS).catch((error) => {
				console.error('Failed to track spotting trends usage:', error);
			});

			triggerTutorial(TUTORIAL_FEATURES.SPOTTING_TRENDS).catch((error) => {
				console.error('Failed to trigger spotting trends tutorial:', error);
			});
		}
	};

	const handleTooltipClose = (event, reason) => {
		// Prevent closing when clicking inside the tooltip (like the arrow button)
		if (tooltipOpen !== null && reason === 'clickAway' && event) {
			// First check the ref flag - this is set synchronously when clicking the arrow
			if (clickingInsideRef.current) {
				// Clicking inside - keep tooltip open
				setTooltipOpen(true);
				return;
			}
			
			// Find the tooltip element immediately
			const tooltipElement = document.querySelector('.MuiTooltip-tooltip');
			
			if (!tooltipElement) {
				// Tooltip element not found, allow close
				setTooltipOpen(false);
				return;
			}
			
			// Check multiple ways to detect if click is inside
			const target = event?.target;
			const relatedTarget = event?.relatedTarget;
			
			// Get event path if available (more reliable than target)
			let eventPath = [];
			if (event?.composedPath) {
				eventPath = event.composedPath();
			} else if (event?.path) {
				eventPath = event.path;
			}
			
			// Check if any element in the event path is inside the tooltip
			const isInside = eventPath.some(node => 
				node === tooltipElement || 
				(node?.classList && node.classList.contains('MuiTooltip-tooltip')) ||
				(node && tooltipElement.contains(node))
			) || 
			(target && (tooltipElement.contains(target) || tooltipElement === target)) ||
			(relatedTarget && (tooltipElement.contains(relatedTarget) || tooltipElement === relatedTarget));
			
			if (isInside) {
				// Clicking inside - keep tooltip open
				setTooltipOpen(true);
			} else {
				// Clicking outside - allow close
				setTooltipOpen(false);
			}
			return;
		}
		// For escape key, allow close
		if (tooltipOpen !== null && reason === 'escapeKeyDown') {
			setTooltipOpen(false);
		}
	};

	if (color && rangesSet) {
		switch (type) {
			case "button":
				return (
					<>
						{!isMobile && (
							<Tooltip
								{...(tooltipOpen !== null ? { open: tooltipOpen } : {})}
								onOpen={handleTooltipOpen}
								onClose={tooltipOpen !== null ? handleTooltipClose : undefined}
								title={
									<TooltipTitle
										comparingAgainst={comparingAgainst}
										comparingAgainstScaled={comparingAgainstScaled}
										numberOfDays={numberOfDays}
										comparisonRanges={comparisonRanges}
										matchingNames={comparingAgainstDifferentFacility && rangesSet && matchingNames ? matchingNames : null}
										comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
										customLabel={customLabel}
										hideProjection={hideProjection}
										onClickingInside={(value) => { clickingInsideRef.current = value; }}
										isTotalOverallCard={isTotalOverallCard}
									/>
								}
								arrow
								disableInteractive={false}
								PopperProps={{
									onClick: (e) => e.stopPropagation(),
									onMouseDown: (e) => e.stopPropagation(),
									sx: {
										'& .MuiTooltip-tooltip': {
											maxWidth: '300px' // Set your desired width
										}
									}
								}}
							>
								<Box
									component={"div"}
									sx={{
										background: color,
										width: "9px",
										height: "18px",
										borderRadius: "2px",
										position: "absolute",
										left: "3px",
										cursor: 'default',
										...sx,
									}}
								/>
							</Tooltip>
						)}
						{isMobile && (
							<Box
								component={"div"}
								sx={{
									background: color,
									width: "9px",
									height: "18px",
									borderRadius: "2px",
									position: "absolute",
									left: "3px",
									cursor: 'pointer',
									...sx,
								}}
								onClick={handleMobileClick}
								onTouchEnd={handleMobileClick}
							/>
						)}
						<MobileColorBoxDialog
							open={mobileDialogOpen}
							onClose={() => setMobileDialogOpen(false)}
							color={color}
							comparingAgainst={comparingAgainst}
							comparingAgainstScaled={comparingAgainstScaled}
							numberOfDays={numberOfDays}
							loading={loading}
							loadingComparison={loadingComparison}
							isLayoutMode={isLayoutMode}
						/>
					</>
				);
			case "div":
				return (
					<>
						{!isMobile && (
							<Tooltip
								title={
									<TooltipTitle
										comparingAgainst={comparingAgainst}
										comparingAgainstScaled={comparingAgainstScaled}
										numberOfDays={numberOfDays}
										comparisonRanges={comparisonRanges}
										matchingNames={comparingAgainstDifferentFacility && rangesSet && matchingNames ? matchingNames : null}
										comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
										customLabel={customLabel}
										hideProjection={hideProjection}
										onClickingInside={(value) => { clickingInsideRef.current = value; }}
										isTotalOverallCard={isTotalOverallCard}
									/>
								}
								arrow
								disableInteractive={false}
								onOpen={handleTooltipOpen}
								PopperProps={{
									onClick: (e) => e.stopPropagation(),
									onMouseDown: (e) => e.stopPropagation(),
									sx: {
										'& .MuiTooltip-tooltip': {
											maxWidth: '400px' // Set your desired width
										}
									}
								}}
							>
								<Box
									component={"div"}
									sx={{
										display: "inline-block",
										background: color,
										width: "11px",
										height: "27px",
										borderRadius: "2px",
										ml: 0.3,
										cursor: 'default',
										...sx,
									}}
								/>
							</Tooltip>
						)}
						{isMobile && (
							<Box
								component={"div"}
								sx={{
									display: "inline-block",
									background: color,
									width: "9px",
									height: "17px",
									borderRadius: "2px",
									ml: 0.3,
									cursor: 'pointer',
									...sx,
								}}
								onClick={handleMobileClick}
								onTouchEnd={handleMobileClick}
							/>
						)}
						<MobileColorBoxDialog
							open={mobileDialogOpen}
							onClose={() => setMobileDialogOpen(false)}
							color={color}
							comparingAgainst={comparingAgainst}
							comparingAgainstScaled={comparingAgainstScaled}
							numberOfDays={numberOfDays}
							loading={loading}
							loadingComparison={loadingComparison}
							isLayoutMode={isLayoutMode}
						/>
					</>
				);

			case "bar":
				return (
					<>
						{!isMobile && (
							<Tooltip
								{...(tooltipOpen !== null ? { open: tooltipOpen } : {})}
								onOpen={handleTooltipOpen}
								onClose={tooltipOpen !== null ? handleTooltipClose : undefined}
								title={
									<TooltipTitle
										comparingAgainst={comparingAgainst}
										comparingAgainstScaled={comparingAgainstScaled}
										numberOfDays={numberOfDays}
										comparisonRanges={comparisonRanges}
										matchingNames={comparingAgainstDifferentFacility && rangesSet && matchingNames ? matchingNames : null}
										comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
										customLabel={customLabel}
										hideProjection={hideProjection}
										onClickingInside={(value) => { clickingInsideRef.current = value; }}
										isTotalOverallCard={isTotalOverallCard}
									/>
								}
								arrow
								disableInteractive={false}
								PopperProps={{
									onClick: (e) => e.stopPropagation(),
									onMouseDown: (e) => e.stopPropagation(),
									sx: {
										'& .MuiTooltip-tooltip': {
											maxWidth: '300px' // Set your desired width
										}
									}
								}}
							>
								<rect
									width="9px"
									height="18px"
									fill={color}
									y={7}
									x={barWidth / 2 - 35}
									style={{ cursor: 'default' }}
								/>
							</Tooltip>
						)}
						{isMobile && (
							<rect
								width="9px"
								height="18px"
								fill={color}
								y={7}
								x={barWidth / 2 - 35}
								style={{ cursor: 'pointer' }}
								onClick={handleMobileClick}
								onTouchEnd={handleMobileClick}
							/>
						)}
						<MobileColorBoxDialog
							open={mobileDialogOpen}
							onClose={() => setMobileDialogOpen(false)}
							color={color}
							comparingAgainst={comparingAgainst}
							comparingAgainstScaled={comparingAgainstScaled}
							numberOfDays={numberOfDays}
							loading={loading}
							loadingComparison={loadingComparison}
							isLayoutMode={isLayoutMode}
						/>
					</>
				);

			case "dayBar":
				return (
					<>
						{!isMobile && (
							<Tooltip
								{...(tooltipOpen !== null ? { open: tooltipOpen } : {})}
								onOpen={handleTooltipOpen}
								onClose={tooltipOpen !== null ? handleTooltipClose : undefined}
								title={
									<TooltipTitle
										comparingAgainst={comparingAgainst}
										comparingAgainstScaled={comparingAgainstScaled}
										numberOfDays={numberOfDays}
										comparisonRanges={comparisonRanges}
										matchingNames={comparingAgainstDifferentFacility && rangesSet && matchingNames ? matchingNames : null}
										comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
										customLabel={customLabel}
										hideProjection={hideProjection}
										onClickingInside={(value) => { clickingInsideRef.current = value; }}
										isTotalOverallCard={isTotalOverallCard}
									/>
								}
								arrow
								disableInteractive={false}
								PopperProps={{
									onClick: (e) => e.stopPropagation(),
									onMouseDown: (e) => e.stopPropagation(),
									sx: {
										'& .MuiTooltip-tooltip': {
											maxWidth: '300px' // Set your desired width
										}
									}
								}}
							>
								<rect
									width="24px"
									height="9px"
									x={bar.width / 4}
									y={bar.height / 2 + 10}
									fill={color}
									style={{ cursor: 'default' }}
								/>
							</Tooltip>
						)}
						{isMobile && (
							<rect
								width="24px"
								height="9px"
								x={bar.width / 4}
								y={bar.height / 2 + 10}
								fill={color}
								style={{ cursor: 'pointer' }}
								onClick={handleMobileClick}
								onTouchEnd={handleMobileClick}
							/>
						)}
						<MobileColorBoxDialog
							open={mobileDialogOpen}
							onClose={() => setMobileDialogOpen(false)}
							color={color}
							comparingAgainst={comparingAgainst}
							comparingAgainstScaled={comparingAgainstScaled}
							numberOfDays={numberOfDays}
							loading={loading}
							loadingComparison={loadingComparison}
							isLayoutMode={isLayoutMode}
						/>
					</>
				);
			case "list":
				return (
					<>
						{!isMobile && (
							<Tooltip
								title={
									<TooltipTitle
										comparingAgainst={comparingAgainst}
										comparingAgainstScaled={comparingAgainstScaled}
										numberOfDays={numberOfDays}
										comparisonRanges={comparisonRanges}
										matchingNames={comparingAgainstDifferentFacility && rangesSet && matchingNames ? matchingNames : null}
										comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
										customLabel={customLabel}
										hideProjection={hideProjection}
										onClickingInside={(value) => { clickingInsideRef.current = value; }}
										isTotalOverallCard={isTotalOverallCard}
										hideArrow={hideArrow}
										isPercentage={isPercentage}
									/>
								}
								arrow
								disableInteractive={false}
								onOpen={handleTooltipOpen}
								PopperProps={{
									onClick: (e) => e.stopPropagation(),
									onMouseDown: (e) => e.stopPropagation(),
								}}
							>
								<Box
									component={"div"}
									sx={{
										display: "inline-block",
										background: color,
										width: "9px",
										height: "18px",
										borderRadius: "2px",
										cursor: 'default',
										...sx,
									}}
								/>
							</Tooltip>
						)}
						{isMobile && (
							<Box
								component={"div"}
								sx={{
									display: "inline-block",
									background: color,
									width: "9px",
									height: "18px",
									borderRadius: "2px",
									cursor: 'pointer',
									...sx,
								}}
								onClick={handleMobileClick}
								onTouchEnd={handleMobileClick}
							/>
						)}
						<MobileColorBoxDialog
							open={mobileDialogOpen}
							onClose={() => setMobileDialogOpen(false)}
							color={color}
							comparingAgainst={comparingAgainst}
							comparingAgainstScaled={comparingAgainstScaled}
							numberOfDays={numberOfDays}
							loading={loading}
							loadingComparison={loadingComparison}
							isLayoutMode={isLayoutMode}
						/>
					</>
				);

			default:
				return null;
		}
	}
	return null;
}
